import { useCallback, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Chip,
  DataTable,
  Icon,
  Menu,
  Meter,
  type Column,
  type MenuItem,
  type Tone,
} from '@w7/ui'
import { AccountCell } from '@/app/AppShell'
import { Amount, FlagChips, Money } from '@/app/bits'
import { limitUsage, round2 } from '@/lib/book'
import { EMPTY, ago, count, lll, money, percent } from '@/lib/format'
import { isAgent, type Account, type AccountStatus } from '@/data/types'
import type { DownlineResult } from '@/data/api'
import { isDirect, zeroingMove } from './settle'

/**
 * The flat downline table.
 *
 * Two surfaces share it. `Direct only` is the SETTLING surface: row selection,
 * labelled Give and Take buttons, and a settlement figure that is itself a
 * click-to-zero button. The match list (whole downline + a filter) is the same
 * table read-only, because a filter produces a list and browsing produces a
 * tree — and a lazily-loaded tree that is also filtered would have to fetch
 * ancestors nobody expanded, giving you a tree with holes in it.
 *
 * The give/take controls are ABSENT on rows that are not direct children, not
 * disabled. The original disabled them and appended a 90-character explanation
 * to the aria-label of every one of 120 rows; the page now says it once, in a
 * quiet line under the toolbar.
 */

export type SortId =
  | 'username'
  | 'creditLimit'
  | 'balance'
  | 'exposure'
  | 'settlement'
  | 'lastActiveAt'
export type SortDirection = 'asc' | 'desc'

export const SORTABLE: { id: SortId; defaultDirection: SortDirection }[] = [
  { id: 'username', defaultDirection: 'asc' },
  { id: 'creditLimit', defaultDirection: 'desc' },
  { id: 'balance', defaultDirection: 'desc' },
  { id: 'exposure', defaultDirection: 'desc' },
  { id: 'settlement', defaultDirection: 'desc' },
  { id: 'lastActiveAt', defaultDirection: 'desc' },
]

/**
 * Status is not money, so it never borrows the money palette: in this console
 * green means the book won and red means it paid out, and a green "Active" chip
 * beside a red balance makes both harder to read.
 */
export const STATUS: Record<AccountStatus, { label: string; tone: Tone }> = {
  active: { label: 'Active', tone: 'info' },
  inactive: { label: 'Inactive', tone: 'muted' },
  suspended: { label: 'Suspended', tone: 'warn' },
  closed: { label: 'Closed', tone: 'muted' },
}

/**
 * Why a row cannot be ticked. Every reason is stated on the disabled checkbox
 * itself rather than the box being removed: a missing box breaks the column and
 * the rows stop scanning as a grid.
 *
 * Suspended accounts stay SELECTABLE on purpose — settling up with a suspended
 * agent is very often exactly why they were suspended.
 */
export function selectionBlock(row: Account, self: Account | undefined): string | null {
  if (!isDirect(row, self)) {
    return `${row.username} sits further down — credit moves only between you and your direct children`
  }
  if (row.settlement === 0) return `${row.username} has nothing outstanding to settle`
  if (row.status === 'closed') return `${row.username}'s account is closed`
  return null
}

export interface DownlineTableProps {
  rows: Account[]
  totals: DownlineResult['totals'] | undefined
  self: Account | undefined
  loading: boolean
  density: 'compact' | 'default'
  sort: SortId
  direction: SortDirection
  onSort: (id: SortId, next: SortDirection) => void
  /** Present only on the direct-only surface. Its absence is what removes bulk settle. */
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void }
  /** Match-list mode: "under ravi_07 › k_master" under the username. */
  lineageOf?: (row: Account) => string | null
  onShowInTree?: (row: Account) => void
  onTransfer: (row: Account, direction: 'give' | 'take', amount?: number) => void
  empty: ReactNode
}

export function DownlineTable({
  rows,
  totals,
  self,
  loading,
  density,
  sort,
  direction,
  onSort,
  selection,
  lineageOf,
  onShowInTree,
  onTransfer,
  empty,
}: DownlineTableProps) {
  const navigate = useNavigate()
  // Below 900px the two labelled buttons do not fit beside the money columns,
  // so they fold into the Menu rather than disappearing — the actions must
  // never become unreachable on a phone.
  const narrow = useNarrow(900)

  const sortHeader = (id: SortId, label: string, align: 'left' | 'right' = 'right') => (
    <SortHeader
      label={label}
      align={align}
      active={sort === id}
      direction={direction}
      defaultDirection={SORTABLE.find((entry) => entry.id === id)?.defaultDirection ?? 'desc'}
      onSort={(next) => onSort(id, next)}
    />
  )

  const columns: Column<Account>[] = [
    {
      id: 'account',
      header: sortHeader('username', 'Agent or member', 'left'),
      width: 240,
      total: totals ? (
        <span className="w7-dim">
          {count(totals.agents)} agents · {count(totals.members)} members
        </span>
      ) : (
        EMPTY
      ),
      cell: (row) => {
        const lineage = lineageOf?.(row)
        return (
          <AccountCell
            username={row.username}
            displayName={row.displayName}
            role={row.role}
            to={`/member/${row.id}`}
            meta={
              <>
                {isAgent(row.role) ? `${count(row.memberCount)} members · ` : null}
                {row.displayName}
                {lineage ? <span className="w7-dl-lineage">{lineage}</span> : null}
              </>
            }
          />
        )
      },
    },
    {
      id: 'creditLimit',
      header: sortHeader('creditLimit', 'Credit limit'),
      numeric: true,
      hideBelow: 900,
      help: 'The ceiling you set on what this account may hold.',
      total: <Amount value={totals?.creditLimit} />,
      cell: (row) => <Amount value={row.creditLimit} />,
    },
    {
      id: 'balance',
      header: sortHeader('balance', 'Balance'),
      numeric: true,
      help: 'What they hold right now. Neither good news nor bad — just a position.',
      total: <Amount value={totals?.balance} />,
      cell: (row) => <Amount value={row.balance} />,
    },
    {
      id: 'exposure',
      header: sortHeader('exposure', 'Exposure'),
      numeric: true,
      hideBelow: 560,
      help:
        'Worst case across their open bets, drawn against their credit limit. ' +
        'The continuously-polled live book is on the Exposure screen.',
      total: totals ? <UsageCell exposure={totals.exposure} creditLimit={totals.creditLimit} /> : EMPTY,
      cell: (row) => <UsageCell exposure={row.exposure} creditLimit={row.creditLimit} />,
    },
    {
      id: 'settlement',
      header: sortHeader('settlement', 'Settlement'),
      numeric: true,
      hideBelow: 720,
      help:
        'One signed figure in place of the old Take and Give pair. Positive is credit you collect ' +
        'from them; negative is credit you owe them. Click a figure on a direct child to settle it ' +
        'to zero.',
      total: totals ? <Money value={round2(totals.toCollect - totals.toPay)} signed /> : EMPTY,
      cell: (row) => <SettlementCell row={row} self={self} onTransfer={onTransfer} />,
    },
    {
      id: 'status',
      header: 'Status',
      hideBelow: 900,
      cell: (row) => (
        <Chip tone={STATUS[row.status].tone} size="sm">
          {STATUS[row.status].label}
        </Chip>
      ),
    },
    {
      id: 'lastActive',
      header: sortHeader('lastActiveAt', 'Last active'),
      numeric: true,
      hideBelow: 1080,
      cell: (row) => (
        <span title={row.lastActiveAt ? lll(row.lastActiveAt) : 'Has never signed in'}>
          {ago(row.lastActiveAt)}
        </span>
      ),
    },
    {
      id: 'flags',
      header: 'Attention',
      hideBelow: 1280,
      help: 'Operational signals only — credit, dormancy and settlement.',
      cell: (row) =>
        row.flags.length === 0 ? <span className="w7-dimmer">Clear</span> : <FlagChips flags={row.flags} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      // 200px, up from 112: two labelled buttons and a menu. The owner's
      // directive — give/take is a labelled BUTTON, not a bare arrow icon.
      width: 200,
      help: 'Move credit, or open the full record.',
      // Deliberately last and never hidden: the table renders row actions inside
      // the final column, so a column that disappeared at 1280px would take the
      // give and take buttons with it on a phone.
      cell: () => null,
    },
  ]

  return (
    <DataTable
      label={
        selection
          ? 'Your direct downline, one row per agent or member'
          : 'Everyone beneath you matching these filters, one row per agent or member'
      }
      rows={rows}
      columns={columns}
      rowKey={(row) => row.id}
      density={density}
      loading={loading}
      skeletonRows={10}
      showTotals
      selectable={
        selection
          ? {
              selected: selection.selected,
              onChange: selection.onChange,
              isSelectable: (row) => selectionBlock(row, self) === null,
              disabledReason: (row) =>
                selectionBlock(row, self) ?? `${row.username} cannot be selected`,
              rowLabel: (row) => `Select ${row.username}`,
            }
          : undefined
      }
      rowTone={(row) =>
        row.flags.includes('exposure-over-limit')
          ? 'loss'
          : row.flags.includes('exposure-near-limit')
            ? 'warn'
            : undefined
      }
      rowActions={(row) => {
        const direct = isDirect(row, self)
        const items: (MenuItem | 'separator')[] = []
        if (direct && narrow) {
          items.push(
            {
              id: 'give',
              label: 'Give credit',
              icon: 'give',
              onSelect: () => onTransfer(row, 'give'),
            },
            {
              id: 'take',
              label: 'Take credit',
              icon: 'take',
              onSelect: () => onTransfer(row, 'take'),
            },
            'separator',
          )
        }
        items.push(
          {
            id: 'open',
            label: isAgent(row.role) ? 'Open agent' : 'Open member',
            icon: 'user',
            onSelect: () => navigate(`/member/${row.id}`),
          },
          {
            id: 'statement',
            label: 'Statement',
            icon: 'transfers',
            onSelect: () => navigate(`/member/${row.id}?tab=statement`),
          },
          {
            id: 'bets',
            label: 'Bets',
            icon: 'sport',
            onSelect: () => navigate(`/member/${row.id}?tab=bets`),
          },
        )
        if (onShowInTree) {
          items.push('separator', {
            id: 'tree',
            label: 'Show in tree',
            icon: 'tree',
            onSelect: () => onShowInTree(row),
          })
        }

        return (
          <>
            {direct && !narrow ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="give"
                  aria-label={`Give credit to ${row.username}`}
                  onClick={() => onTransfer(row, 'give')}
                >
                  Give
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="take"
                  aria-label={`Take credit from ${row.username}`}
                  onClick={() => onTransfer(row, 'take')}
                >
                  Take
                </Button>
              </>
            ) : null}
            <Menu label={`More for ${row.username}`} align="end" items={items} />
          </>
        )
      }}
      empty={empty}
    />
  )
}

/* -------------------------------------------------------------- fragments -- */

/**
 * The settlement figure IS the button, on a direct child with something
 * outstanding. The operator almost always moves exactly the remaining amount,
 * so the number they are already reading is the number they want to move.
 */
function SettlementCell({
  row,
  self,
  onTransfer,
}: {
  row: Account
  self: Account | undefined
  onTransfer: (row: Account, direction: 'give' | 'take', amount?: number) => void
}) {
  const move = isDirect(row, self) ? zeroingMove(row) : null
  if (!move) return <Money value={row.settlement} signed />

  const verb = move.direction === 'take' ? 'Take' : 'Give'
  const preposition = move.direction === 'take' ? 'from' : 'to'
  return (
    <button
      type="button"
      className="w7-settle"
      aria-label={`${verb} ${money(move.amount)} ${preposition} ${row.username} to settle to zero`}
      title={`Opens the transfer popup with ${money(move.amount)} to ${move.direction} already filled in — you can still change it.`}
      onClick={() => onTransfer(row, move.direction, move.amount)}
    >
      <Money value={row.settlement} signed />
      <Icon name={move.direction === 'take' ? 'take' : 'give'} size={12} />
      <span className="w7-settle-hint w7-micro">Settle</span>
    </button>
  )
}

/**
 * Sorting is driven from the header into the QUERY, not into the table.
 *
 * The table sorts the rows it is handed, which is one page of twenty-five; on a
 * downline of a hundred and twenty that would quietly mean "sort this page",
 * which is worse than the original's honest lack of sorting.
 */
function SortHeader({
  label,
  align,
  active,
  direction,
  defaultDirection,
  onSort,
}: {
  label: string
  align: 'left' | 'right'
  active: boolean
  direction: SortDirection
  defaultDirection: SortDirection
  onSort: (next: SortDirection) => void
}) {
  const next: SortDirection = active ? (direction === 'asc' ? 'desc' : 'asc') : defaultDirection
  return (
    <button
      type="button"
      className="w7-th-sort"
      data-align={align}
      aria-label={
        active
          ? `${label}, sorted ${direction === 'asc' ? 'lowest first' : 'highest first'}. Sort ${next === 'asc' ? 'lowest' : 'highest'} first.`
          : `Sort by ${label}`
      }
      onClick={() => onSort(next)}
    >
      {label}
      <Icon
        name={active && direction === 'asc' ? 'chevron-up' : 'chevron-down'}
        size={12}
        style={{ opacity: active ? 1 : 0.28 }}
      />
    </button>
  )
}

/** Exposure against the limit that governs it — the column the original left at 0. */
export function UsageCell({ exposure, creditLimit }: { exposure: number; creditLimit: number }) {
  const usage = limitUsage(exposure, creditLimit)
  return (
    <span className="w7-col" style={{ gap: 3, alignItems: 'flex-end' }}>
      <Amount value={exposure} />
      <Meter
        value={usage}
        height={4}
        label={`${percent(usage, 0)} of a ${money(creditLimit, { round: true })} credit limit`}
      />
    </span>
  )
}

/**
 * A media query as state, without an effect or a resize listener of our own.
 * The row actions genuinely change shape below 900px — the buttons fold into
 * the Menu — and rendering both and hiding one with CSS would leave a phone
 * operator tabbing through two invisible buttons.
 */
export function useNarrow(maxWidth: number): boolean {
  const query = `(max-width: ${maxWidth}px)`
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', notify)
      return () => mql.removeEventListener('change', notify)
    },
    [query],
  )
  const snapshot = useMemo(
    () => () =>
      typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
    [query],
  )
  return useSyncExternalStore(subscribe, snapshot, () => false)
}
